import { ASNFormData } from './types';
import { useMenuSettings } from '../../../contexts/MenuSettingsContext';

interface ShipmentDetailsStepProps {
  formData: ASNFormData;
  onChange: (field: keyof ASNFormData, value: string) => void;
  /** CR-6038: true when the ASN is built from supplier-imported delivery lines.
   *  Their typed weight is always used; for other ASNs it is used only when a
   *  part has no weight in the Parts master (CR-6041). */
  isSupplierImport?: boolean;
  /** CR-8190: the haulier the customer appointed on the call-off. When set it
   *  is the carrier, shown read-only — the supplier does not choose one. */
  haulierName?: string | null;
  /** CR-8190: true while the call-off's haulier is being looked up. */
  carrierLoading?: boolean;
  /** CR-8190: true when the call-off could not be checked for a haulier. */
  carrierLookupFailed?: boolean;
}

export function ShipmentDetailsStep({
  formData, onChange, isSupplierImport = false,
  haulierName = null, carrierLoading = false, carrierLookupFailed = false,
}: ShipmentDetailsStepProps) {
  // CR-9120: one date on every ASN, the day it is expected to arrive. A
  // call-off's ASN used to call it the pickup date, but it is stored and shown
  // everywhere else as the expected delivery, and it now defaults to the
  // delivery date the haulier agreed.
  const dateLabel = 'Expected Delivery Date';
  // CR-6160: when the paid ASN logistics-tracking module is on, courier +
  // tracking number are required (they drive live tracking); when off they are
  // optional and a premium hint is shown.
  const { isFeatureEnabled } = useMenuSettings();
  const trackingRequired = isFeatureEnabled('asn_logistics_tracking');
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="asn-courier" className="block text-sm font-medium mb-2">
            Courier {trackingRequired && <span className="text-red-500">*</span>}
          </label>
          {haulierName ? (
            <>
              <input
                id="asn-courier"
                type="text"
                value={haulierName}
                readOnly
                className="w-full px-3 py-2 border rounded-lg bg-muted/40 cursor-not-allowed focus:outline-none"
              />
              <p className="text-xs text-muted-foreground mt-1">Set by the customer on the call-off.</p>
            </>
          ) : carrierLoading ? (
            <div role="status" className="w-full px-3 py-2 border rounded-lg bg-muted/40 text-sm text-muted-foreground">
              Checking the call-off for a haulier…
            </div>
          ) : (
            <>
              <select
                id="asn-courier"
                value={formData.courier}
                onChange={(e) => onChange('courier', e.target.value)}
                required={trackingRequired}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="fedex">FedEx</option>
                <option value="ups">UPS</option>
                <option value="dhl">DHL</option>
                <option value="tnt">TNT</option>
                <option value="dpd">DPD</option>
                <option value="other">Other</option>
              </select>
              {carrierLookupFailed && (
                <p className="text-xs text-amber-700 mt-1">
                  Couldn&apos;t check the call-off for a haulier. If the customer has set one, choose Other and enter its name.
                </p>
              )}
            </>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">
            Tracking Number {trackingRequired && <span className="text-red-500">*</span>}
          </label>
          <input
            type="text"
            value={formData.tracking_number}
            onChange={(e) => onChange('tracking_number', e.target.value)}
            required={trackingRequired}
            placeholder="1234567890"
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>
      {!trackingRequired && (
        <p className="text-xs text-amber-700">
          Live courier tracking is a premium add-on — contact us to enable it.
        </p>
      )}

      <div>
        <label className="block text-sm font-medium mb-2">Invoice Number</label>
        <input
          type="text"
          value={formData.invoice_number}
          onChange={(e) => onChange('invoice_number', e.target.value)}
          placeholder="e.g. DRW042604"
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Your delivery-note / invoice reference for accounts traceability.
        </p>
      </div>

      {!haulierName && !carrierLoading && formData.courier === 'other' && (
        <div>
          <label className="block text-sm font-medium mb-2">
            Courier Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.courier_other}
            onChange={(e) => onChange('courier_other', e.target.value)}
            required
            placeholder="Enter courier name"
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Tracking details for custom couriers may not be available.
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">
            Transport Method <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.method_of_transport}
            onChange={(e) => onChange('method_of_transport', e.target.value)}
            required
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="road">Road</option>
            <option value="rail">Rail</option>
            <option value="air">Air</option>
            <option value="sea">Sea</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">
            {dateLabel} <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={formData.expected_delivery_date}
            onChange={(e) => onChange('expected_delivery_date', e.target.value)}
            required
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Weight (kg)</label>
          {/* CR-6041: editable on every ASN. Supplier imports keep CR-6038's
              rule (the typed weight is used); for the others it is used only
              when a part has no weight in the Parts master. */}
          <input
            type="number"
            min="0"
            step="0.01"
            value={formData.weight_kg}
            onChange={(e) => onChange('weight_kg', e.target.value)}
            placeholder="e.g. 125.50"
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="text-xs text-muted-foreground mt-1">
            {isSupplierImport
              ? 'Optional — total shipment weight for carbon reporting.'
              : 'Used only when one or more parts have no weight in the Parts master. Enter the total shipment weight.'}
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Distance (km)</label>
          <div className="w-full px-3 py-2 border rounded-lg bg-muted/40 text-sm text-muted-foreground">
            Auto-calculated on save (supplier ↔ delivery site)
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Notes (Optional)</label>
        <textarea
          value={formData.notes}
          onChange={(e) => onChange('notes', e.target.value)}
          rows={3}
          placeholder="Add any additional shipping information..."
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
    </div>
  );
}
